import { forwardRef, useId, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from "react";
import { cn } from "../../lib/cn";

const base =
  "w-full rounded-md border border-line-strong bg-surface px-3 text-base text-fg placeholder:text-fg-3 transition-colors duration-150 hover:border-fg-3/40 focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent-soft disabled:opacity-60 aria-[invalid=true]:border-danger";

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input({ className, ...rest }, ref) {
  return <input ref={ref} className={cn(base, "h-10", className)} {...rest} />;
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea({ className, ...rest }, ref) {
  return <textarea ref={ref} className={cn(base, "min-h-20 resize-none py-2.5", className)} {...rest} />;
});

/** Label + control + inline error/hint. */
export function Field({
  label,
  error,
  hint,
  children,
  className,
  id: idProp,
  counter,
}: {
  label: ReactNode;
  error?: string | null;
  hint?: ReactNode;
  children: (props: { id: string; "aria-invalid": boolean; "aria-describedby"?: string }) => ReactNode;
  className?: string;
  id?: string;
  counter?: ReactNode;
}) {
  const auto = useId();
  const id = idProp ?? auto;
  const descId = error || hint ? `${id}-desc` : undefined;
  return (
    <div className={cn("space-y-1.5", className)}>
      <div className="flex items-baseline justify-between">
        <label htmlFor={id} className="text-sm font-medium text-fg-2">
          {label}
        </label>
        {counter && <span className="text-xs tabular text-fg-3">{counter}</span>}
      </div>
      {children({ id, "aria-invalid": !!error, "aria-describedby": descId })}
      {(error || hint) && (
        <p id={descId} className={cn("text-xs", error ? "text-danger" : "text-fg-3")} role={error ? "alert" : undefined}>
          {error || hint}
        </p>
      )}
    </div>
  );
}
